import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";

import { ArticleRichTextEditor } from "@/components/ui/admin/articles/ArticleRichTextEditor";
import { createLegalService } from "@/core/services/factory";
import {
  LEGAL_DOCUMENT_SLUGS,
  type LegalDocument,
  type LegalDocumentSlug,
} from "@/core/services/types/legal.types";
import { htmlToMarkdown, markdownToHtml } from "@/lib/legal-markdown";
import { useTranslations } from "@/lib/useTranslations";

type Draft = {
  document_id?: string;
  slug: LegalDocumentSlug;
  title: string;
  content_md: string;
  version: string;
  locale: string;
  is_published: boolean;
  effective_at: string;
};

function formatDate(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function suggestNextVersion(current: string): string {
  const match = /^(\d+)\.(\d+)$/.exec(current.trim());
  if (!match) return `${current}-next`;
  return `${match[1]}.${Number(match[2]) + 1}`;
}

function emptyDraft(
  slug: LegalDocumentSlug,
  defaultTitles: Record<LegalDocumentSlug, string>,
): Draft {
  return {
    slug,
    title: defaultTitles[slug],
    content_md: "",
    version: "1.0",
    locale: "fr",
    is_published: false,
    effective_at: new Date().toISOString().slice(0, 16),
  };
}

function draftFromDoc(doc: LegalDocument): Draft {
  return {
    document_id: doc.id,
    slug: doc.slug,
    title: doc.title,
    content_md: doc.content_md,
    version: doc.version,
    locale: doc.locale,
    is_published: doc.is_published,
    effective_at: doc.effective_at.slice(0, 16),
  };
}

function pickDocForSlug(
  rows: LegalDocument[],
  slug: LegalDocumentSlug,
): LegalDocument | undefined {
  return (
    rows.find((d) => d.slug === slug && d.is_published) ??
    rows.find((d) => d.slug === slug)
  );
}

const RICH_TEXT_LABELS = {
  toolbar: "Barre d'outils de l'éditeur",
  bold: "Gras",
  italic: "Italique",
  underline: "Souligné",
  heading2: "Titre 2",
  heading3: "Titre 3",
  bulletList: "Liste à puces",
  orderedList: "Liste numérotée",
  blockquote: "Citation",
};

export function LegalPanel() {
  const t = useTranslations("legal");
  const legalService = useMemo(() => createLegalService(), []);

  const defaultTitles = useMemo(
    () =>
      ({
        cgus: t("slugs.cgus"),
        legal: t("slugs.legal"),
        privacy: t("slugs.privacy"),
        about: t("slugs.about"),
      }) satisfies Record<LegalDocumentSlug, string>,
    [t],
  );

  const [docs, setDocs] = useState<LegalDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] =
    useState<LegalDocumentSlug>("cgus");
  const [draft, setDraft] = useState<Draft>(() =>
    emptyDraft("cgus", {
      cgus: "Conditions générales d'utilisation",
      legal: "Mentions légales",
      privacy: "Politique de confidentialité",
      about: "À propos",
    }),
  );

  const load = useCallback(async () => {
    setError(null);
    const rows = await legalService.listAll();
    setDocs(rows);
    setDraft((prev) => {
      if (prev.document_id) {
        const same = rows.find((d) => d.id === prev.document_id);
        if (same) return draftFromDoc(same);
      }
      const picked = pickDocForSlug(rows, prev.slug);
      return picked ? draftFromDoc(picked) : emptyDraft(prev.slug, defaultTitles);
    });
    setLoading(false);
  }, [defaultTitles, legalService]);

  useEffect(() => {
    void load();
  }, [load]);

  const versionsForSlug = useMemo(() => {
    return docs
      .filter((d) => d.slug === selectedSlug)
      .slice()
      .sort((a, b) => {
        if (a.is_published !== b.is_published) return a.is_published ? -1 : 1;
        return (
          new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
        );
      });
  }, [docs, selectedSlug]);

  const selectSlug = (slug: LegalDocumentSlug) => {
    if (slug === selectedSlug) return;
    setSelectedSlug(slug);
    setInfo(null);
    setError(null);
    const picked = pickDocForSlug(docs, slug);
    setDraft(picked ? draftFromDoc(picked) : emptyDraft(slug, defaultTitles));
  };

  const selectVersion = (doc: LegalDocument) => {
    setInfo(null);
    setError(null);
    setSelectedSlug(doc.slug);
    setDraft(draftFromDoc(doc));
  };

  const onSave = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const effectiveAt = draft.effective_at
        ? new Date(draft.effective_at).toISOString()
        : new Date().toISOString();
      const saved = await legalService.upsert({
        document_id: draft.document_id,
        slug: draft.slug,
        title: draft.title.trim(),
        content_md: htmlToMarkdown(draft.content_md),
        version: draft.version.trim() || "1.0",
        locale: "fr",
        is_published: draft.is_published,
        effective_at: effectiveAt,
      });
      if (!saved) {
        setError(t("saveError"));
        return;
      }
      setInfo(
        draft.is_published ? t("saveSuccessPublished") : t("saveSuccess"),
      );
      setSelectedSlug(saved.slug);
      setDraft(draftFromDoc(saved));
      const rows = await legalService.listAll();
      setDocs(rows);
    } catch {
      setError(t("saveError"));
    } finally {
      setSaving(false);
    }
  };

  const editorKey =
    draft.document_id ?? `new:${draft.slug}:${draft.version}:${draft.locale}`;

  if (loading) {
    return (
      <div className="rounded-xl border border-black/10 bg-white px-4 py-10 text-center text-sm text-neutral-500 shadow-sm">
        {t("loading")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}
      {info && !error && (
        <p className="rounded-xl border border-black/10 bg-zinc-50 px-3 py-2 text-sm text-zinc-800">
          {info}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {LEGAL_DOCUMENT_SLUGS.map((slug) => {
          const count = docs.filter((d) => d.slug === slug).length;
          const active = selectedSlug === slug;
          return (
            <button
              key={slug}
              type="button"
              onClick={() => selectSlug(slug)}
              className={`rounded-xl border px-4 py-2 text-sm transition-colors ${
                active
                  ? "border-black/20 bg-neutral-100 font-medium text-whart-text"
                  : "border-black/10 text-neutral-600 hover:bg-neutral-50 hover:text-whart-text"
              }`}
            >
              {t(`slugs.${slug}`)}
              <span className="ml-2 text-xs text-neutral-500">
                {t("versionCount", { count })}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="rounded-xl border border-black/10 bg-white p-3 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">
              {t("history")}
            </p>
            <button
              type="button"
              onClick={() => {
                setDraft((d) => ({
                  ...d,
                  document_id: undefined,
                  version: suggestNextVersion(
                    versionsForSlug[0]?.version ?? d.version,
                  ),
                  is_published: false,
                  effective_at: new Date().toISOString().slice(0, 16),
                }));
                setInfo(null);
              }}
              className="rounded-xl bg-yellow-100 px-2 py-1 text-[11px] font-medium text-zinc-900 transition hover:bg-amber-100"
            >
              {t("newVersion")}
            </button>
          </div>

          {versionsForSlug.length === 0 ? (
            <p className="text-xs text-neutral-500">{t("noVersions")}</p>
          ) : (
            <ul className="max-h-[min(70vh,44rem)] space-y-1.5 overflow-y-auto">
              {versionsForSlug.map((doc) => {
                const selected = draft.document_id === doc.id;
                return (
                  <li key={doc.id}>
                    <button
                      type="button"
                      onClick={() => selectVersion(doc)}
                      className={`w-full rounded-xl border px-3 py-2 text-left transition-colors ${
                        selected
                          ? "border-black/20 bg-neutral-50"
                          : "border-transparent hover:border-black/10 hover:bg-zinc-50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-whart-text">
                          v{doc.version}
                        </span>
                        {doc.is_published ? (
                          <span className="rounded-xl bg-whart-published/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-whart-published">
                            {t("published")}
                          </span>
                        ) : (
                          <span className="rounded-xl bg-neutral-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-neutral-500">
                            {t("draft")}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-[11px] text-neutral-500">
                        {formatDate(doc.updated_at)}
                      </p>
                      <p className="mt-0.5 truncate text-[11px] text-neutral-600">
                        {doc.title}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <form
          onSubmit={(e) => void onSave(e)}
          className="space-y-4 rounded-xl border border-black/10 bg-white p-4 shadow-sm"
        >
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <label className="block space-y-1.5 sm:col-span-2 xl:col-span-2">
              <span className="text-xs uppercase tracking-wider text-neutral-500">
                {t("titleField")}
              </span>
              <input
                required
                value={draft.title}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, title: e.target.value }))
                }
                className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-whart-text outline-none focus:border-black/20"
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs uppercase tracking-wider text-neutral-500">
                {t("versionField")}
              </span>
              <input
                required
                value={draft.version}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, version: e.target.value }))
                }
                className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-whart-text outline-none focus:border-black/20"
              />
            </label>
            <label className="block space-y-1.5 sm:col-span-2 xl:col-span-2">
              <span className="text-xs uppercase tracking-wider text-neutral-500">
                {t("effectiveAt")}
              </span>
              <input
                type="datetime-local"
                value={draft.effective_at}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, effective_at: e.target.value }))
                }
                className="w-full rounded-xl border border-black/10 bg-white px-3 py-2 text-sm text-whart-text outline-none focus:border-black/20"
              />
            </label>
            <label className="flex items-end gap-2 pb-2 text-sm text-neutral-700 sm:col-span-2 xl:col-span-2">
              <input
                type="checkbox"
                checked={draft.is_published}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, is_published: e.target.checked }))
                }
                className="rounded border-black/20"
              />
              {t("publishCheckbox")}
            </label>
          </div>

          <div className="space-y-2">
            <span className="text-xs uppercase tracking-wider text-neutral-500">
              {t("content")}
            </span>
            <ArticleRichTextEditor
              editorKey={editorKey}
              value={markdownToHtml(draft.content_md)}
              placeholder={t("contentPlaceholder")}
              labels={RICH_TEXT_LABELS}
              onChange={(html) =>
                setDraft((d) => ({ ...d, content_md: htmlToMarkdown(html) }))
              }
            />
          </div>

          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-black/10 bg-white/95 pt-3 backdrop-blur-sm">
            <p className="text-xs text-neutral-500">
              {draft.document_id
                ? t("editingVersion", {
                    version: draft.version,
                    updatedAt: formatDate(
                      docs.find((d) => d.id === draft.document_id)
                        ?.updated_at ?? new Date().toISOString(),
                    ),
                  })
                : t("newVersionHint", { version: draft.version })}
            </p>
            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-yellow-100 px-4 py-2 text-sm font-medium text-zinc-900 transition hover:bg-amber-100 disabled:opacity-50"
            >
              {saving ? t("saving") : t("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
